import { FC, useMemo } from "react";

import { Dropdown, FileUpload, TextArea } from "capitalrx-components-2.0/lib/forms";
import { Icon } from "capitalrx-components-2.0/lib/general";
import { Grid, Shelf, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Body, Semibold, Regular, Paragraph, Subheading } from "capitalrx-components-2.0/lib/typography";
import { formatDate } from "capitalrx-utilities";
import { FileEntry } from "capitalrx-components-2.0/lib/forms";

import { ProviderManagement } from "@/api/interfaces";
import { PROVIDER_MANAGEMENT_LAYOUT } from "@/constants";
import { getDocumentsByType } from "../../helper";


export interface ProviderDocumentsDataForm {
    readonly authorization_letter: {
        files: FileEntry[]
        notes: string
    };
    readonly bank_letterhead_voided_check: {
        type: string
        files: FileEntry[]
        notes: string
    };
}


export interface FieldsDocumentsProps {
  readonly readOnly?: boolean;
  readonly provider: ProviderManagement.ProviderSchema;
  readonly documents: ProviderManagement.DocumentSchema[];
}

export const FieldsDocuments: FC<FieldsDocumentsProps> = ({ readOnly, provider, documents }) => {
  const { twelve, sixFull, isCompressed } = useResponsive(PROVIDER_MANAGEMENT_LAYOUT);
  const StackOrShelf = isCompressed ? Stack : Shelf;

  const documentsByType = useMemo(() => {
    return getDocumentsByType(documents);
  }, [documents]);

  const displayDocument = (document: ProviderManagement.DocumentSchema) => {
    return (
      <Stack spacing="mini">
        <StackOrShelf spacing="small">
          <Shelf spacing="small">
            <Icon name="document" />
            <Body>{document.file_name}</Body>
            <Shelf.Spacer />
          </Shelf>
          <Shelf.Spacer />
          <Body>{formatDate(document.created_at, "MM/DD/YYYY hh:mm:ss A", true, true)}</Body>
        </StackOrShelf>
        <StackOrShelf spacing="small">
          <Semibold>Notes: </Semibold>
          <Paragraph>{document.notes}</Paragraph>
          <Shelf.Spacer />
        </StackOrShelf>
      </Stack>
    );
  };

  return (
    <Stack spacing="large">
      <Stack>
        <Semibold>Required Documents:</Semibold>
        <Stack spacing="none">
          <Regular>1. Authorization Letter for EFT - A signed letter authorizing electronic funds transfer.</Regular>
          <Regular>2. Bank Letterhead OR Voided Check - A document from your bank confirming account details.</Regular>
        </Stack>
      </Stack>
      <Stack>
        <Subheading>Authorization Letter</Subheading>
        {documentsByType.authLetter && displayDocument(documentsByType.authLetter)}
        {!readOnly
          && <Grid>
            <Grid.Spread columns={twelve}>
              <FileUpload.Field
                name="authorization_letter.files"
                allowMultiple={true}
                allowedExtensions={[".docx", ".pdf", ".jpeg", ".jpg", ".png"]}
                testId="authorization_letter_files"
                label="Select File(s)"
                readOnly={readOnly}
                maxSize={4500000}
                required={!documentsByType.authLetter}
              />
              <TextArea.Field
                label="Notes"
                testId="authorization_letter_notes"
                name="authorization_letter.notes"
                lines={6}
                readOnly={readOnly}
              />
            </Grid.Spread>
          </Grid>
        }
      </Stack>
      <Stack>
        <Subheading>Bank Letterhead or Voided Check</Subheading>
        {documentsByType.letterhead && displayDocument(documentsByType.letterhead)}
        {!readOnly
          && <Grid>
            <Grid.Span columns={sixFull}>
              <Dropdown.Field
                label="Type"
                name="bank_letterhead_voided_check.type"
                testId="bank_letterhead_voided_check_type"
                required
                readOnly={readOnly}
              >
                <Dropdown.Option value={ProviderManagement.DOCUMENT_TYPE_VALUES.BANK_LETTERHEAD}>Bank Letterhead</Dropdown.Option>
                <Dropdown.Option value={ProviderManagement.DOCUMENT_TYPE_VALUES.VOIDED_CHECK}>Voided Check</Dropdown.Option>
              </Dropdown.Field>
            </Grid.Span>
            <Grid.Break />
            <Grid.Spread columns={twelve}>
              <FileUpload.Field
                name="bank_letterhead_voided_check.files"
                allowMultiple={true}
                allowedExtensions={[".docx", ".pdf", ".jpeg", ".jpg", ".png"]}
                testId="bank_letterhead_voided_check_files"
                label="Select File(s)"
                readOnly={readOnly}
                maxSize={4500000}
                required={!documentsByType.letterhead}
              />
              <TextArea.Field
                label="Notes"
                testId="bank_letterhead_voided_check_notes"
                name="bank_letterhead_voided_check.notes"
                lines={6}
                readOnly={readOnly}
              />
            </Grid.Spread>
          </Grid>
        }
      </Stack>
    </Stack>
  );
};
